import * as lib from '~/dependencies';
import { colors } from '~/layout';

type GridLayout = 'single' | 'double' | 'triple' | 'quad';

export const Grid = lib.styled.div<{ layout: GridLayout }>`
  display: grid;
  gap: 2px;
  border-radius: 8px;
  overflow: hidden;
  max-width: 270px;
  margin-bottom: 10px;

  ${({ layout }: any) => {
    switch (layout) {
      case 'single':
        return `
          grid-template-columns: 1fr;
          > * { height: 165px; }
        `;
      case 'double':
        return `
          grid-template-columns: 1fr 1fr;
          > * { height: 165px; }
        `;
      case 'triple':
        return `
          grid-template-columns: 1fr 1fr;
          > *:first-child {
            grid-column: span 2;
            height: 140px;
          }
          > *:not(:first-child) {
            height: 110px;
          }
        `;
      case 'quad':
      default:
        return `
          grid-template-columns: 1fr 1fr;
          > * { height: 130px; }
        `;
    }
  }}
`;

export const GridItem = lib.styled.div`
  position: relative;
  overflow: hidden;
  cursor: pointer;
  background: ${colors.lightGray18 ?? '#f0f0f0'};

  &:hover img,
  &:hover video {
    transform: scale(1.04);
  }
`;

export const GridThumb = lib.styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: transform 180ms ease;
`;

export const GridVideoThumb = lib.styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: transform 180ms ease;
`;

export const GridVideoPlaceholder = lib.styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #1a1a1a;
`;

export const GridVideoPlayOverlay = lib.styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.2);
  pointer-events: none;

  svg {
    width: 36px;
    height: 36px;
    color: #fff;
    filter: drop-shadow(0 2px 8px rgba(0, 0, 0, 0.55));
  }
`;

export const GridAudioThumb = lib.styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  background: linear-gradient(135deg, #2a3f4a 0%, #3d5a6a 100%);
  padding: 8px;
  box-sizing: border-box;

  svg {
    width: 36px;
    height: 36px;
    color: rgba(255, 255, 255, 0.7);
    flex-shrink: 0;
  }
`;

export const GridPdfThumb = lib.styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  background: linear-gradient(135deg, #4a1a1a 0%, #6a2a2a 100%);
  padding: 8px;
  box-sizing: border-box;

  svg {
    width: 36px;
    height: 36px;
    color: rgba(255, 255, 255, 0.85);
    flex-shrink: 0;
  }
`;

export const GridThumbLabel = lib.styled.span`
  color: rgba(255, 255, 255, 0.75);
  font-size: 10px;
  font-weight: 500;
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
`;

export const LightboxPdfFrame = lib.styled.iframe`
  width: 85vw;
  height: 85vh;
  max-width: 100%;
  max-height: 100%;
  border: none;
  border-radius: 8px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.4);
  background: #fff;
  cursor: default;
`;

export const OverflowBadge = lib.styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  font-size: 24px;
  font-weight: 700;
  letter-spacing: -0.5px;
  pointer-events: none;
`;

export const LightboxBackdrop = lib.styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.85);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: zoom-out;
  padding: 80px 20px 20px;
  box-sizing: border-box;
`;

export const LightboxImage = lib.styled.img`
  max-width: 85vw;
  max-height: 80vh;
  object-fit: contain;
  border-radius: 8px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.4);
  cursor: default;
`;

export const LightboxVideo = lib.styled.video`
  max-width: 85vw;
  max-height: 80vh;
  border-radius: 8px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.4);
  background: #000;
  outline: none;
  cursor: default;
`;

export const LightboxAudio = lib.styled.div`
  margin-top: -250px;
  background: #1e2c35;
  border-radius: 12px;
  padding: 20px 24px;
  min-width: 280px;
  cursor: default;
`;

export const LightboxNavButton = lib.styled.button<{
  direction: 'left' | 'right';
}>`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  ${({ direction }: { direction: string | undefined }) =>
    direction === 'left' ? 'left: 16px;' : 'right: 16px;'}
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.15);
  border: none;
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 150ms ease;
  z-index: 2;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }

  svg {
    width: 28px;
    height: 28px;
  }
`;

export const LightboxCounter = lib.styled.div`
  position: absolute;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  color: rgba(255, 255, 255, 0.8);
  font-size: 13px;
  font-weight: 500;
  background: rgba(0, 0, 0, 0.4);
  padding: 4px 12px;
  border-radius: 12px;
  cursor: default;
`;

export const LightboxActions = lib.styled.div`
  position: absolute;
  top: 20px;
  right: 20px;
  display: flex;
  align-items: center;
  gap: 10px;
  z-index: 2;
`;

export const LightboxIconButton = lib.styled.button`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.15);
  border: none;
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 150ms ease;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }

  svg {
    width: 22px;
    height: 22px;
  }
`;

export const LightboxMenu = lib.styled.div`
  position: absolute;
  top: 70px;
  right: 20px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25);
  min-width: 180px;
  overflow: hidden;
  z-index: 2;
`;

export const LightboxMenuItem = lib.styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 12px 16px;
  background: transparent;
  border: none;
  cursor: pointer;
  font-family: inherit;
  font-size: 14px;
  color: ${colors.darkGray13 ?? '#333'};
  text-align: left;
  transition: background 150ms ease;

  &:hover {
    background: ${colors.lightGray18 ?? '#f5f5f5'};
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;
